// frontend/src/router/index.ts
// ============================================================================
// RAVERS NETWORK - Маршрутизация приложения (Vue Router 4)
// Включает: Безопасные проверки сессий без бесконечных циклов перезагрузки
// ============================================================================

import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean;
    guestOnly?: boolean;
    title?: string;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/friends',
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: {
      guestOnly: true,
      title: 'Вход',
    },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/RegisterView.vue'),
    meta: {
      guestOnly: true,
      title: 'Регистрация',
    },
  },
  {
    path: '/friends',
    name: 'friends',
    component: () => import('@/views/FriendsView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Контакты',
    },
  },
  {
    path: '/chat/:targetId',
    name: 'chat',
    component: () => import('@/views/ChatView.vue'),
    meta: {
      requiresAuth: true,
      title: 'P2P Чат',
    },
  },
  {
    path: '/verify',
    name: 'audio-verify',
    component: () => import('@/views/AudioVerifyView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Аудио Экспертиза',
    },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

// ============================================================================
// ГЛОБАЛЬНЫЙ НАВИГАЦИОННЫЙ ГВАРД БЕЗ БЛОКИРОВОК
// ============================================================================
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore();

  // Инициализируем сессию только один раз при старте
  if (!authStore.isInitialized) {
    try {
      await authStore.fetchCurrentUser();
    } catch (e) {
      console.warn('Инициализация сессии пропущена:', e);
    }
  }

  // Установка заголовка вкладки
  document.title = to.meta.title ? `${to.meta.title} | Ravers Network` : 'Ravers Network';

  const isAuthenticated = Boolean(authStore.isAuthenticated);

  // 1. Попытка зайти в закрытый раздел без авторизации
  if (to.meta.requiresAuth && !isAuthenticated) {
    if (to.name !== 'login') {
      return next({ name: 'login' });
    }
    return next();
  }

  // 2. Авторизованный пользователь пытается зайти на логин/регистрацию
  if (to.meta.guestOnly && isAuthenticated) {
    if (to.name !== 'friends') {
      return next({ name: 'friends' });
    }
    return next();
  }

  next();
});
